// src/Pages/ServiciosMultiReporte.jsx
import React, {
    useCallback,
    useMemo,
    useRef,
    useState,
    forwardRef,
    useImperativeHandle,
    useEffect,
} from "react";
import {
    Button,
    ListGroup,
    ListGroupItem,
    Spinner,
    Badge,
    FormGroup,
    Input,
} from "reactstrap";
import { usePDF } from "react-to-pdf";

import useReporteSnapshot from "./../../hooks/useReporteSnapshot";
import ReporteStaticHTML from "../reportes/ReporteStaticHTML";
import ReporteDoc from "../reportes/ReporteDoc";
import { pdf } from "@react-pdf/renderer";
import Swal from "sweetalert2";
import axios from "../../utils/axiosConfig";

/* -------------------- Config -------------------- */
const MAX_ITEMS = 10;

/* -------------------- Helpers -------------------- */
function normalizeServiceId(raw) {
    if (!raw) return "";
    const t = String(raw).trim().toUpperCase();
    const m = t.match(/^SERV-(\d{1,})$/);
    if (m) return `SERV-${m[1].slice(0, 12).padStart(8, "0")}`;
    const digits = t.replace(/\D/g, "").slice(0, 12);
    if (!digits) return "";
    return `SERV-${digits.padStart(8, "0")}`;
}

// Convierte un string con varios tokens separados por espacios -> IDs normalizados únicos
function parseMany(raw) {
    if (!raw) return [];
    const tokens = String(raw).trim().split(/\s+/);
    const ids = tokens
        .map(normalizeServiceId)
        .filter(Boolean);

    // únicos manteniendo orden
    const seen = new Set();
    const unique = [];
    for (const id of ids) {
        if (!seen.has(id)) {
            seen.add(id);
            unique.push(id);
        }
    }
    return unique;
}

/* -------------------------------------------------
   PdfGenerator: carga snapshot y dispara toPDF()
   ------------------------------------------------- */
function sleep(ms) {
    return new Promise((r) => setTimeout(r, ms));
}

const PdfGenerator = forwardRef(function PdfGenerator({ contractId, imgProxy }, ref) {
    const { loading, error, snapshot } = useReporteSnapshot(contractId);

    // Refs que siempre contienen el valor MÁS RECIENTE del hook:
    const loadingRef = useRef(loading);
    const errorRef = useRef(error);
    const snapRef = useRef(snapshot);

    useEffect(() => { loadingRef.current = loading; }, [loading]);
    useEffect(() => { errorRef.current = error; }, [error]);
    useEffect(() => { snapRef.current = snapshot; }, [snapshot]);

    useImperativeHandle(ref, () => ({
        async generate({ filename = `reporte-${contractId}.pdf`, autoDownload = false } = {}) {
            // Esperar a que termine de cargar el snapshot (con timeout)
            const start = Date.now();
            const TIMEOUT = 60_000; // 60s
            while (loadingRef.current) {
                if (Date.now() - start > TIMEOUT) {
                    throw new Error("Timeout generando snapshot");
                }
                await sleep(120);
            }

            if (errorRef.current) throw new Error(errorRef.current);
            if (!snapRef.current) throw new Error("Snapshot vacío");

            // Render -> Blob (sin usar el DOM)
            const blob = await pdf(
                <ReporteDoc
                    data={snapRef.current}
                    logoUrl="https://static.wixstatic.com/media/9a4347_a8dbd9ccfecd4eb2b4239eadc7369c73~mv2.png"
                />
            ).toBlob();

            if (autoDownload) {
                // descarga inmediata opcional
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = filename;
                document.body.appendChild(a);
                a.click();
                a.remove();
                URL.revokeObjectURL(url);
            }

            return blob; // por si prefieres manejar la descarga afuera
        },
    }));

    return null; // No renderiza nada en pantalla
});

/* -------------------- Componente principal -------------------- */
export default function ServiciosMultiReporte() {
    const [contrato, setContrato] = useState("");
    const [items, setItems] = useState([]); // [{ id, downloading, error }]
    const [bulkLoading, setBulkLoading] = useState(false);
    const [feedback, setFeedback] = useState("");
    const inputRef = useRef(null);

    // refs dinámicos para cada item -> PdfGenerator
    const pdfRefs = useRef(new Map()); // key=id, value=refObj

    const canAddMore = items.length < MAX_ITEMS;
    const remaining = useMemo(() => MAX_ITEMS - items.length, [items.length]);

    const handleContrato = useCallback(async (val) => {
        setContrato(val);
        setFeedback("");
    }, []);

    // Agrega uno o varios IDs desde el campo de texto (separados por espacios)

    const addFromInput = useCallback(async () => {
        if (!contrato?.trim()) return;

        if (!canAddMore) {
            setFeedback(`Máximo ${MAX_ITEMS} servicios.`);
            return;
        }

        // 👇 AQUÍ EL CAMBIO IMPORTANTE:
        // YA NO armamos payloadText aquí, dejamos que validarListaContratos
        // se encargue de interpretar lo que el usuario escribió (uno o varios).
        const { existentes = [], noExistentes = [] } =
            await validarListaContratos(contrato);

        if (!existentes.length) {
            setFeedback("Ningún contrato existente para agregar.");
            return;
        }

        const currentIds = new Set(items.map(x => x.id));
        const toAdd = [];
        const skippedDup = [];
        const skippedOverflow = [];

        for (const id of existentes) {
            if (currentIds.has(id) || toAdd.includes(id)) { skippedDup.push(id); continue; }
            if (toAdd.length >= remaining) { skippedOverflow.push(id); continue; }
            toAdd.push(id);
        }

        if (toAdd.length) {
            setItems(prev => [
                ...prev,
                ...toAdd.map(id => ({ id, downloading: false, error: null })),
            ]);
        }

        const msgs = [];
        if (toAdd.length) msgs.push(`Agregados: ${toAdd.length}`);
        if (skippedDup.length) msgs.push(`Omitidos duplicados: ${skippedDup.length}`);
        if (skippedOverflow.length) msgs.push(`Omitidos por límite (${MAX_ITEMS}): ${skippedOverflow.length}`);
        if (noExistentes.length) msgs.push(`No existen: ${noExistentes.length}`);
        if (!msgs.length) msgs.push("Nada para agregar.");

        setFeedback(msgs.join(" · "));
        setContrato("");
        inputRef.current?.focus();
    }, [contrato, items, remaining, canAddMore]);




    const onSubmit = useCallback(async (e) => {
        e?.preventDefault?.();
        await addFromInput();
    }, [addFromInput]);

    const handleKeyDown = useCallback(async (e) => {
        if (e.key === "Enter") {
            e.preventDefault();
            await addFromInput();
        }
    }, [addFromInput]);

    const removeItem = useCallback((id) => {
        setItems((prev) => prev.filter((x) => x.id !== id));
        pdfRefs.current.delete(id);
    }, []);

    // Asegura que exista un ref para un id
    const ensureRef = useCallback((id) => {
        if (!pdfRefs.current.has(id)) {
            pdfRefs.current.set(id, React.createRef());
        }
        return pdfRefs.current.get(id);
    }, []);

    //Validar si lista de contratos existe separados por comas 
    /** Extrae tokens entre comillas simples; si no hay, cae a CSV.
 * Normaliza: asegura prefijo SERV-.
 */
    // "'A','B','C'" -> ["A","B","C"]; soporta también "A B C" o "A,B,C"
    const toArrayFromPayload = (text) => {
        if (!text) return [];
        const str = String(text).trim();
        const re = /'([^']*)'/g;
        const out = [];
        let m;
        while ((m = re.exec(str)) !== null) out.push(m[1].trim());
        if (out.length) return out.filter(Boolean);
        return str.split(/[,\s]+/).map(s => s.trim()).filter(Boolean);
    };

    // Array -> "'A','B','C'"
    const serializarContratos = (arr) => arr.map(v => `'${v}'`).join(',');

    // Divide por espacios en crudo
    const splitBySpacesRaw = (s) =>
        String(s || "").trim().split(/\s+/).filter(Boolean);

    // Extrae IDs del recordset (objetos con ContractID o variantes)
    const idsFromRecordset = (recordset) => {
        if (!Array.isArray(recordset)) return [];
        if (recordset.length === 0) return [];
        if (typeof recordset[0] === "string") {
            return recordset.map(s => String(s).trim()).filter(Boolean);
        }
        const campos = ["ContractID", "contractId", "contrato", "serviceId", "codigo", "codigoServicio"];
        return recordset.map(row => {
            for (const k of campos) if (row?.[k]) return String(row[k]).trim();
            return null;
        }).filter(Boolean);
    };

    const joinBold = (arr) => arr.map(s => `<b>${s}</b>`).join(", ");


    /** Valida lista y consulta existencia en backend. */
    // --- Validador: sin chequear formato local, sólo backend ---
    // Valida SOLO con el endpoint; calcula faltantes localmente
    async function validarListaContratos(lista) {
        // Respeta "'A','B'"; si viene "A B C" lo convertimos a "'A','B','C'" sin tocar tokens
        const payloadText = (/,/.test(lista) && /'/.test(lista))
            ? lista.trim()
            : serializarContratos(splitBySpacesRaw(lista));

        if (!payloadText) {
            Swal.fire("Lista vacía", "No hay contratos para validar.", "warning");
            return { ok: false, existentes: [], noExistentes: [], normalizadosStr: "" };
        }

        // Originales en el orden que escribió el usuario
        const originales = toArrayFromPayload(payloadText);

        const base = (process.env.REACT_APP_SERVER || "").replace(/\/?$/, "/");
        try {
            const { data } = await axios.post(
                `${base}operaciones/getexistencontratos`,
                { contratos: payloadText } // ej: 'SERV-00485221','SERV-00486327','SERV-88888'
            );

            // El backend devuelve SOLO los existentes en recordset (objetos con ContractID)
            const recordset = data?.recordset ?? data?.data?.recordset ?? [];
            const existentesBackend = idsFromRecordset(recordset);

            // Preservamos el orden del usuario y evitamos dups
            const setExist = new Set(existentesBackend);
            const existentes = originales.filter(id => setExist.has(id));

            // Faltantes = originales - existentes
            const setExistOrdenado = new Set(existentes); // para O(1)
            const noExistentes = originales.filter(id => !setExistOrdenado.has(id));

            if (noExistentes.length) {
                Swal.fire({
                    icon: "warning",
                    title: "Contratos no encontrados",
                    html: `No existen en el sistema:<br>${joinBold(noExistentes)}`,
                });
            }

            return {
                ok: noExistentes.length === 0,
                existentes,                 // agrega SOLO estos
                noExistentes,               // para feedback
                normalizadosStr: payloadText, // exactamente lo enviado al endpoint
            };
        } catch (error) {
            Swal.fire("Error", "No se pudo validar los contratos en el servidor.", "error");
            return {
                ok: false,
                existentes: [],
                noExistentes: [],
                normalizadosStr: payloadText,
                error: error?.message,
            };
        }
    }



    // Descargar uno
    const generateOne = useCallback(async (id) => {
        console.time(`generateOne ${id}`);
        const ref = ensureRef(id);

        setItems(prev =>
            prev.map(x => (x.id === id ? { ...x, downloading: true, error: null } : x))
        );

        try {
            if (!ref.current?.generate) {
                throw new Error("Generador no está listo");
            }

            // Genera el PDF (Blob)
            const blob = await ref.current.generate();
            if (!blob) throw new Error("No se generó el PDF");

            // Fuerza la descarga
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `reporte-${id}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);

            setItems(prev =>
                prev.map(x => (x.id === id ? { ...x, downloading: false, error: null } : x))
            );
            console.timeEnd(`generateOne ${id}`);
        } catch (err) {
            setItems(prev =>
                prev.map(x =>
                    x.id === id
                        ? { ...x, downloading: false, error: err?.message || "Error al generar PDF" }
                        : x
                )
            );
        }
    }, [ensureRef]);


    // Descargar todos (secuencial)
    const generateAll = useCallback(async () => {
        if (!items.length) return;
        setBulkLoading(true);
        console.time("generateAll");
        try {
            for (const it of items) {
                // eslint-disable-next-line no-await-in-loop
                await generateOne(it.id);
            }
        } finally {
            setBulkLoading(false);
            console.timeEnd("generateAll");
        }
    }, [items, generateOne]);

    return (
        <div className="w-100">
            {/* Encabezado */}
            <div className="d-flex justify-content-between align-items-center mb-2">
                <h6 className="mb-0 fw-semibold">Múltiples reportes de servicio</h6>
                <Badge color={remaining > 0 ? "secondary" : "danger"} className="rounded-pill">
                    {remaining} libres / {MAX_ITEMS} máx
                </Badge>
            </div>

            {/* Formulario */}
            <form onSubmit={onSubmit} className="w-100">
                <FormGroup className="mb-2">
                    <Input
                        type="text"
                        id="input1"
                        className="w-100"
                        placeholder="SERV-12345678 12345679 SERV-12345680"
                        style={{ textTransform: "uppercase", boxSizing: "border-box" }}
                        value={contrato}
                        onChange={(e) => handleContrato(e.target.value)}
                        onKeyDown={handleKeyDown}
                        innerRef={inputRef}
                        autoFocus
                    />
                    <div className="form-text">
                        Puedes pegar/escribir **varios** separados por espacio. Se aceptan “12345678” o “SERV-12345678”. Máx {MAX_ITEMS}.
                    </div>
                </FormGroup>

                <div className="d-grid">
                    <Button onClick={onSubmit} disabled={!canAddMore} color="primary">
                        Cargar informes
                    </Button>
                </div>
            </form>

            {/* Feedback */}
            <div className="small mt-2" style={{ color: feedback ? "#0d6efd" : "#6c757d" }}>
                {feedback ? feedback : `Puedes agregar hasta ${MAX_ITEMS}. Restantes: ${remaining}.`}
            </div>

            {/* Lista (contenedor con scroll) */}
            <div
                className="mt-3 w-100"
                style={{
                    height: 400,
                    overflowY: "auto",
                    overflowX: "hidden",
                    padding: 8,
                    background: "#f8f9fa",
                    borderRadius: 12,
                    boxShadow: "inset 0 0 0 1px rgba(0,0,0,.06)",
                }}
            >
                {items.length === 0 ? (
                    <div className="d-flex flex-column align-items-center justify-content-center text-muted h-100">
                        <div className="fw-semibold">No hay contratos seleccionados</div>
                        <div className="small">Agrega uno o varios para comenzar.</div>
                    </div>
                ) : (
                    <ListGroup flush>
                        {items.map((it) => {
                            const ref = ensureRef(it.id);
                            return (
                                <ListGroupItem
                                    key={it.id}
                                    className="d-flex align-items-center justify-content-between flex-wrap gap-2 border rounded-3 mb-2"
                                    style={{ background: "#fff", borderColor: "#e9ecef" }}
                                >
                                    {/* ID */}
                                    <div className="d-flex align-items-center gap-2">
                                        <Badge
                                            color="secondary"
                                            className="text-monospace px-3 py-2"
                                            style={{ fontSize: "0.95rem" }}
                                        >
                                            {it.id}
                                        </Badge>
                                        {it.error && <span className="text-danger small">Error: {it.error}</span>}
                                    </div>

                                    {/* Acciones */}
                                    <div className="d-flex flex-column flex-sm-row gap-2">
                                        <Button
                                            size="sm"
                                            color="success"
                                            className="px-3"
                                            disabled={it.downloading}
                                            onClick={() => generateOne(it.id)}
                                            title="Descargar PDF individual"
                                        // style={{ color: "white", background: "#006699" }}
                                        >
                                            {it.downloading ? (
                                                <>
                                                    <Spinner size="sm" /> <span className="ms-1">Generando…</span>
                                                </>
                                            ) : (
                                                "Descargar PDF"
                                            )}
                                        </Button>
                                        <Button
                                            size="sm"
                                            color="outline-secondary"
                                            className="px-3"
                                            onClick={() => removeItem(it.id)}
                                            title="Quitar de la lista"
                                        >
                                            Quitar
                                        </Button>
                                    </div>

                                    {/* Worker oculto por item */}
                                    <PdfGenerator ref={ref} contractId={it.id} />
                                </ListGroupItem>
                            );
                        })}
                    </ListGroup>
                )}
            </div>

            {/* Acciones globales */}
            <div className="d-flex flex-column flex-sm-row justify-content-center gap-2 mt-3 w-100">
                <Button
                    color="primary"
                    disabled={!items.length || bulkLoading}
                    onClick={generateAll}
                    className="px-4"
                >
                    {bulkLoading ? (
                        <>
                            <Spinner size="sm" /> <span className="ms-1">Generando todos…</span>
                        </>
                    ) : (
                        "Descargar todos"
                    )}
                </Button>
                <Button
                    color="light"
                    disabled={!items.length || bulkLoading}
                    onClick={() => setItems([])}
                    className="px-4"
                >
                    Limpiar lista
                </Button>
            </div>
        </div>
    );
}
